// Vertikom PrimeNG preset, shared by frontend and frontend-landingpages

import { $dt, definePreset } from '@primeng/themes';
import Aura from '@primeng/themes/aura';
import { AuraBaseDesignTokens } from '@primeng/themes/aura/base';
import { Preset } from '@primeng/themes/types';

import { generateColorPalette, generatePaletteTokens } from '@fsf/util';

const accent = generateColorPalette('#45ae10');
const dark = generateColorPalette('#1f3465');

const preset: Preset<AuraBaseDesignTokens> = {
  extend: {
    primitive: {
      accent,
      dark,
    },
    semantic: {
      secondary: {
        50: '{accent.50}',
        100: '{accent.100}',
        200: '{accent.200}',
        300: '{accent.300}',
        400: '{accent.400}',
        500: '{accent.500}',
        600: '{accent.600}',
        700: '{accent.700}',
        800: '{accent.800}',
        900: '{accent.900}',
        950: '{accent.950}',
      },
      green: {
        50: '{accent.50}',
        100: '{accent.100}',
        200: '{accent.200}',
        300: '{accent.300}',
        400: '{accent.400}',
        500: '{accent.500}',
        600: '{accent.600}',
        700: '{accent.700}',
        800: '{accent.800}',
        900: '{accent.900}',
        950: '{accent.950}',
      },
    },
  },
  primitive: {
    borderRadius: {
      xl: '14px',
    },
  },
  semantic: {
    primary: {
      50: '{dark.50}',
      100: '{dark.100}',
      200: '{dark.200}',
      300: '{dark.300}',
      400: '{dark.400}',
      500: '{dark.500}',
      600: '{dark.600}',
      700: '{dark.700}',
      800: '{dark.800}',
      900: '{dark.900}',
      950: '{dark.950}',
    },
    colorScheme: {
      light: {
        text: { color: '{primary.500}' },
        mask: { background: 'rgba(0, 0, 0, 0.85)' },
      },
      dark: {
        primary: {
          color: '#fff',
        },
        mask: { background: 'rgba(0, 0, 0, 0.85)' },
        surface: {
          50: '{dark.50}',
          100: '{dark.100}',
          200: '{dark.200}',
          300: '{dark.300}',
          400: '{dark.400}',
          500: '{dark.500}',
          600: '{dark.600}',
          700: '{dark.700}',
          800: '{dark.800}',
          900: '{dark.900}',
          950: '{dark.950}',
        },
        text: {
          color: '#fff',
          mutedColor: '#ffffffaa',
        },
        navigation: {
          item: {
            icon: {
              color: '{muted.color}',
              focusColor: '#fff',
            },
          },
        },
      },
    },
    formField: {
      borderRadius: '{border.radius.xl}',
      paddingY: '0.4rem',
    },
  },
  components: {
    button: {
      root: {
        label: {
          fontWeight: '700',
        },
      },
      colorScheme: {
        light: {
          root: {
            secondary: {
              background: '{surface.200}',
              hoverBackground: '{surface.300}',
              activeBackground: '{surface.400}',
              color: '{primary.500}',
              hoverColor: '{primary.500}',
            },
            success: {
              color: '{primary.700}',
              hoverColor: '{primary.700}',
            },
            info: {
              background: '{blue.500}',
              hoverBackground: '{blue.600}',
              activeBackground: '{blue.700}',
              borderColor: '{blue.500}',
              hoverBorderColor: '{blue.600}',
              activeBorderColor: '{blue.700}',
              color: '#ffffff',
            },
          },
          outlined: {
            info: {
              borderColor: '{blue.500}',
              hoverBackground: '{blue.50}',
              activeBackground: '{blue.100}',
              color: '{blue.500}',
            },
          },
          text: {
            contrast: {
              color: '{secondary.500}',
              hoverBackground: '#b1ff002a', //secondary transparent
              activeBackground: '#b1ff002a', //secondary transparent
            },
          },
        },
        dark: {
          root: {
            primary: {
              background: '{primary.100}',
              hoverBackground: '{primary.200}',
              activeBackground: '{primary.200}',
              borderColor: '{primary.100}',
              hoverBorderColor: '{primary.200}',
              color: '{primary.500}',
              hoverColor: '{primary.500}',
            },
            success: {
              hoverBackground: '{green.600}',
              hoverBorderColor: '{green.600}',
              color: '{primary.700}',
              hoverColor: '{primary.700}',
            },
            info: {
              background: '{blue.500}',
              hoverBackground: '{blue.400}',
              activeBackground: '{blue.300}',
              borderColor: '{blue.500}',
              hoverBorderColor: '{blue.400}',
              activeBorderColor: '{blue.300}',
              color: '#ffffff',
            },
          },
          outlined: {
            primary: {
              borderColor: '{primary.100}',
            },
            info: {
              borderColor: '{blue.500}',
              hoverBackground:
                'color-mix(in srgb, {blue.500}, transparent 84%)',
              activeBackground:
                'color-mix(in srgb, {blue.500}, transparent 76%)',
              color: '{blue.500}',
            },
          },
        },
      },
    },
    datatable: {
      headerCell: {
        padding: '0.3rem 1rem',
      },
      bodyCell: {
        padding: '0.3rem 1rem',
      },
      columnTitle: {
        fontWeight: '600',
      },
      // Header stands out from body rows; zebra via [stripedRows] on p-table
      // - row background is surface.0 (light) / surface.900 (dark), stripes must differ
      colorScheme: {
        light: {
          headerCell: {
            background: '{surface.200}',
            borderColor: '{surface.300}',
          },
          row: {
            stripedBackground: '{surface.100}',
          },
        },
        dark: {
          headerCell: {
            background: '{surface.800}',
            borderColor: '{surface.600}',
          },
          row: {
            stripedBackground: '{surface.950}',
          },
        },
      },
    },
    menubar: {
      root: {
        borderRadius: '0',
      },
      colorScheme: {
        light: {
          root: {
            background: '{primary.500}',
            borderColor: 'transparent',
          },
          item: {
            color: '#fff',
            focusBackground: 'transparent',
          },
        },
      },
    },
    panelmenu: {
      root: { gap: '0' },
      panel: {
        first: {
          borderWidth: '0',
        },
        last: {
          borderWidth: '0',
        },
        borderWidth: '0',
        padding: '0 0.25rem',
      },
      item: {
        padding: '0.5rem 0.75rem',
      },
      submenu: {
        indent: '1rem',
      },
      colorScheme: {
        light: {
          root: {},
        },
      },
      css: ({ dt }: { dt: typeof $dt }) => `
        .layout-sidebar {
            box-shadow: 0px 2px 15px -3px rgba(0, 0, 0, 0.1);
        }
        .p-panelmenu-header-link {
            display: flex;
            flex-direction: row;
        }
        .p-panelmenu-header-link > chevrondownicon,
        .p-panelmenu-header-link > chevronrighticon {
            order: 99;
            margin-left: auto;
            margin-right: 0;
        }
        `,
    },
    card: {
      title: {
        fontWeight: '700',
      },
      colorScheme: {
        light: {
          root: {
            background: '{surface.100}',
            shadow: '0px 2px 15px -3px rgba(0, 0, 0, 0.1)',
          },
        },
        dark: {
          root: { background: '{primary.500}' },
        },
      },
    },
    chip: {
      root: {
        paddingY: '0.3rem',
      },
      colorScheme: {
        light: {
          root: {
            background: '#f5f5f5',
          },
        },
      },
    },
    tag: {
      colorScheme: {
        light: {
          secondary: {
            background: '{surface.200}',
          },
        },
      },
    },
    autocomplete: {
      chip: { borderRadius: '16px' },
    },
    breadcrumb: {
      root: {
        padding: '0.375rem 0',
        background: 'transparent',
      },
    },
    slider: {
      colorScheme: {
        light: {
          handle: {
            content: {
              background: '{accent.500}',
              hoverBackground: '{accent.600}',
            },
          },
          range: {
            background: '{accent.500}',
          },
        },
        dark: {
          handle: {
            background: '#fff',
            hoverBackground: '#fff',
            content: {
              background: '{accent.500}',
              hoverBackground: '{accent.600}',
            },
          },
          track: { background: '#fff' },
          range: {
            background: '{accent.500}',
          },
        },
      },
      handle: {
        width: '35px',
        height: '35px',
        content: {
          width: '27px',
          height: '27px',
        },
      },
    },
    galleria: {
      root: {
        borderWidth: '0px',
      },
    },
    accordion: {
      colorScheme: {
        light: {
          panel: {
            borderColor: 'transparent',
          },
          header: {
            background: '{surface.100}',
            hoverBackground: '{surface.100}',
            activeBackground: '{surface.100}',
            activeHoverBackground: '{surface.100}',
          },
          content: {
            background: '{surface.100}',
          },
        },
        dark: {
          panel: {
            borderColor: 'transparent',
          },
          header: {
            background: '{primary.500}',
            hoverBackground: '{primary.500}',
            activeBackground: '{primary.500}',
            activeHoverBackground: '{primary.500}',
          },
          content: {
            background: '{primary.500}',
          },
        },
      },
    },
  },
};

export const colors = {
  dark: dark[500],
  accent: accent[500],
  palette: [
    '#1f3465',
    '#24353D',
    '#DDFF91',
    '#0E1518',
    '#45ae10',
    '#476600',
    '#166DFF',
    '#3A03B0',
    '#C4256A',
    '#D12C34',
    '#EA7D1F',
    '#F1AC27',
    '#E76F51',
    '#2A9D8F',
    '#E9C46A',
    '#577590',
    '#B5838D',
    '#6D6875',
  ],
  darkPalette: [
    '#5B8FD9',
    '#6A8B99',
    '#DDFF91',
    '#0E1518',
    '#45ae10',
    '#00a2bf',
    '#166DFF',
    '#3A03B0',
    '#C4256A',
    '#D12C34',
    '#EA7D1F',
    '#F1AC27',
    '#F4845F',
    '#52B788',
    '#F2CC8F',
    '#83A5C0',
    '#D4A5A5',
    '#9A8C98',
  ],
  map: {
    location: accent[500],
  },
};

export const links = {
  logoSrc: 'assets/images/Vertikom_Logo_white.svg',
  logoAlt: 'Logo',
  impressum: 'https://wp.vertikom.ch/impressum/',
};

// Aura uses orange for warn in button/tag/badge but yellow in message/toast
// - align button/tag/badge to yellow: only their warn tokens, palette swapped
const warnOnly = (tokens: object): object =>
  Object.fromEntries(
    Object.entries(tokens).flatMap(([key, value]) => {
      if (key === 'warn') return [[key, value]];
      if (typeof value !== 'object' || value === null) return [];
      const nested = warnOnly(value);
      return Object.keys(nested).length ? [[key, nested]] : [];
    }),
  );
const yellowWarn = Object.fromEntries(
  (['button', 'tag', 'badge'] as const).map((component) => [
    component,
    JSON.parse(JSON.stringify(warnOnly(Aura.components?.[component] ?? {})).replace(/{orange\./g, '{yellow.')),
  ]),
);

export const CustomPreset = definePreset(Aura, preset, { components: yellowWarn });
